import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  useColorModeValue,
  Text,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  useToast,
} from '@chakra-ui/react';
import { getData, postData } from '../../utils/httpClient';

const CreateCourseDiscountModal = ({ isOpen, onClose, onConfirm }) => {
  const [formData, setFormData] = useState({
    studentId: '',
    courseId: '',
    discountPercentage: 0,
    approvalDate: '',
    concept: '',
  });
  const [studentsWithCourses, setStudentsWithCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  const textColor = useColorModeValue('navy.700', 'white');
  const brandStars = useColorModeValue('brand.500', 'brand.400');
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchStudentsWithCourses();
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.studentId) {
      const selectedStudent = studentsWithCourses.find(
        student => student.id === parseInt(formData.studentId)
      );
      setAvailableCourses(selectedStudent?.courses || []);
      setFormData(prev => ({ ...prev, courseId: '' })); // Reset course selection
    } else {
      setAvailableCourses([]);
    }
  }, [formData.studentId, studentsWithCourses]);

  const fetchStudentsWithCourses = async () => {
    try {
      const response = await getData('/course-discounts/students-with-courses');
      setStudentsWithCourses(response?.payload || []);
    } catch (error) {
      toast({
        title: 'Error al obtener estudiantes',
        description: 'No se pudieron cargar los estudiantes con cursos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      courseId: '',
      discountPercentage: 0,
      approvalDate: '',
      concept: '',
    });
    setAvailableCourses([]);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.studentId || !formData.courseId || !formData.discountPercentage || !formData.approvalDate || !formData.concept) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor complete todos los campos',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.discountPercentage <= 0 || formData.discountPercentage > 100) {
      toast({
        title: 'Porcentaje inválido',
        description: 'El porcentaje debe estar entre 1 y 100',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const discountData = {
        studentId: parseInt(formData.studentId),
        courseId: parseInt(formData.courseId),
        discountPercentage: parseFloat(formData.discountPercentage),
        approvalDate: formData.approvalDate,
        concept: formData.concept.trim(),
      };

      const response = await postData('/course-discounts', discountData);
      
      if (response.status === 201) {
        toast({
          title: 'Descuento creado exitosamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onConfirm();
        handleClose();
      } else {
        throw new Error(response.message || 'Error al crear el descuento');
      }
    } catch (error) {
      toast({
        title: 'Error al crear descuento',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getSelectedStudentName = () => {
    const student = studentsWithCourses.find(s => s.id === parseInt(formData.studentId));
    return student ? `${student.firstName} ${student.lastName}` : '';
  };

  const getSelectedCourseName = () => {
    const course = availableCourses.find(c => c.id === parseInt(formData.courseId));
    return course ? course.subjectDescription : '';
  };

  // Get today's date for default and min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text color={textColor} fontSize="24px" fontWeight="700">
            Nuevo Descuento de Curso
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <FormControl isRequired>
              <FormLabel color={textColor} fontWeight="600">
                Estudiante
              </FormLabel>
              <Select
                placeholder="Seleccione un estudiante"
                value={formData.studentId}
                onChange={(e) => handleInputChange('studentId', e.target.value)}
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
              >
                {studentsWithCourses.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.document} - {student.firstName} {student.lastName}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor} fontWeight="600">
                Curso/Dictado
              </FormLabel>
              <Select
                placeholder="Seleccione un curso"
                value={formData.courseId}
                onChange={(e) => handleInputChange('courseId', e.target.value)}
                isDisabled={!formData.studentId}
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
              >
                {availableCourses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.subjectDescription} ({new Date(course.startDate).toLocaleDateString()} - {new Date(course.endDate).toLocaleDateString()})
                  </option>
                ))}
              </Select>
              {formData.studentId && availableCourses.length === 0 && (
                <Text fontSize="sm" color="orange.500" mt={2}>
                  Este estudiante no tiene cursos asignados
                </Text>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor} fontWeight="600">
                Porcentaje de Descuento (%)
              </FormLabel>
              <NumberInput
                value={formData.discountPercentage}
                onChange={(value) => handleInputChange('discountPercentage', value)}
                min={0}
                max={100}
                precision={2}
                step={0.1}
              >
                <NumberInputField
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "teal.400" }}
                  _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor} fontWeight="600">
                Fecha de Aprobación
              </FormLabel>
              <Input
                type="date"
                value={formData.approvalDate}
                onChange={(e) => handleInputChange('approvalDate', e.target.value)}
                max={today} // No permitir fechas futuras
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor} fontWeight="600">
                Concepto/Motivo
              </FormLabel>
              <Textarea
                value={formData.concept}
                onChange={(e) => handleInputChange('concept', e.target.value)}
                placeholder="Ingrese el motivo o concepto del descuento..."
                rows={4}
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
              />
            </FormControl>

            {/* Resumen */}
            {formData.studentId && formData.courseId && formData.discountPercentage > 0 && (
              <Box p={4} bg="blue.50" borderRadius="lg" borderWidth="1px" borderColor="blue.200">
                <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={2}>
                  Resumen del Descuento
                </Text>
                <Text fontSize="sm" color="blue.600">
                  <strong>Estudiante:</strong> {getSelectedStudentName()}
                </Text>
                <Text fontSize="sm" color="blue.600">
                  <strong>Curso:</strong> {getSelectedCourseName()}
                </Text>
                <Text fontSize="sm" color="blue.600">
                  <strong>Descuento:</strong> {formData.discountPercentage}%
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            variant="ghost" 
            mr={3} 
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button 
            bg={brandStars}
            color="white"
            _hover={{ bg: brandStars }}
            onClick={handleSubmit}
            isDisabled={
              !formData.studentId || 
              !formData.courseId || 
              !formData.discountPercentage || 
              !formData.approvalDate || 
              !formData.concept.trim()
            }
          >
            Crear Descuento
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateCourseDiscountModal;
